import { customInstance } from "@/lib/axios";

/** Single React Query key for application detail + stage rail (dedupes network). */
export function applicationQueryKey(id: string | undefined) {
  return ["application", id] as const;
}

export function applicationAsyncStatusQueryKey(id: string | undefined) {
  return ["application-async-status", id] as const;
}

export async function fetchApplication(id: string, isArchived: boolean) {
  const url = isArchived
    ? `/api/applications/${id}/?is_archived=true`
    : `/api/applications/${id}/`;
  const { data } = await customInstance.get(url);
  return data;
}

export interface ApplicationAsyncStatus {
  stage: string;
  has_in_progress_paynet: boolean;
  has_in_progress_credit: boolean;
  has_pending_scans: boolean;
  fetching_signed_pdf: boolean;
  sos_pending: boolean;
  poll_active: boolean;
  signed_app_pdf_s3_key: string | null;
  paynet_reports: { id: number; status: string }[];
  owners: {
    id: number;
    owner_id: number;
    credit_pull_status: string | null;
    credit_pull_error: string | null;
  }[];
  documents: { id: number; scan_status: string }[];
  sos_registry: Record<string, unknown> | null;
}

export async function fetchApplicationAsyncStatus(
  id: string,
  isArchived: boolean
): Promise<ApplicationAsyncStatus> {
  const url = isArchived
    ? `/api/applications/${id}/async-status/?is_archived=true`
    : `/api/applications/${id}/async-status/`;
  const { data } = await customInstance.get<ApplicationAsyncStatus>(url);
  return data;
}

/** Merge async-status poll into cached full application (avoids refetching entire detail). */
export function mergeApplicationAsyncStatus<T extends Record<string, unknown>>(
  old: T,
  status: ApplicationAsyncStatus
): T {
  const next = {
    ...old,
    stage: status.stage,
    signed_app_pdf_s3_key: status.signed_app_pdf_s3_key,
    sos_registry: status.sos_registry ?? (old as { sos_registry?: unknown }).sos_registry,
  } as T;

  const company = (old as { company?: { paynet_reports?: { id: number; status: string }[] } })
    .company;
  if (company?.paynet_reports && status.paynet_reports?.length) {
    const byId = Object.fromEntries(status.paynet_reports.map((r) => [r.id, r.status]));
    (next as { company: typeof company }).company = {
      ...company,
      paynet_reports: company.paynet_reports.map((r) =>
        byId[r.id] != null ? { ...r, status: byId[r.id] } : r
      ),
    };
  }

  const owners = (old as {
    owners?: {
      id: number;
      owner?: { credit_pull_status?: string; credit_pull_error?: string | null };
    }[];
  }).owners;
  if (owners?.length && status.owners?.length) {
    const byAoId = Object.fromEntries(status.owners.map((o) => [o.id, o]));
    (next as { owners: typeof owners }).owners = owners.map((ao) => {
      const patch = byAoId[ao.id];
      if (!patch || !ao.owner) return ao;
      return {
        ...ao,
        owner: {
          ...ao.owner,
          credit_pull_status: patch.credit_pull_status ?? ao.owner.credit_pull_status,
          credit_pull_error: patch.credit_pull_error ?? ao.owner.credit_pull_error,
        },
      };
    });
  }

  const documents = (old as { documents?: { id: number; scan_status: string }[] }).documents;
  if (documents?.length && status.documents?.length) {
    const byId = Object.fromEntries(status.documents.map((d) => [d.id, d.scan_status]));
    (next as { documents: typeof documents }).documents = documents.map((d) =>
      byId[d.id] != null ? { ...d, scan_status: byId[d.id] } : d
    );
  }

  return next;
}

export function applicationNeedsAsyncPolling(app: {
  company?: { paynet_reports?: { status: string }[] };
  owners?: { owner?: { credit_pull_status?: string } }[];
  documents?: { scan_status: string }[];
  application_signed?: boolean;
  signed_app_pdf_s3_key?: string | null;
  sos_registry?: { eligible?: boolean; status?: string } | null;
}): boolean {
  const hasInProgressPaynet = app.company?.paynet_reports?.some(
    (r) => r.status === "IN_PROGRESS" || r.status === "NOT_STARTED"
  );
  const hasInProgressCredit = app.owners?.some(
    (ao) => ao.owner?.credit_pull_status === "IN_PROGRESS"
  );
  const hasPendingScans = app.documents?.some((d) => d.scan_status === "PENDING");
  const fetchingSignedPdf = Boolean(app.application_signed && !app.signed_app_pdf_s3_key);
  const sos = app.sos_registry;
  const sosPending = Boolean(
    sos?.eligible && (sos.status === "queued" || sos.status === "running" || sos.status === "pending")
  );
  return Boolean(
    hasInProgressPaynet || hasInProgressCredit || hasPendingScans || fetchingSignedPdf || sosPending
  );
}
